import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { ButtonBase, Paper, Typography } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useSnackbar } from 'notistack';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { Apollo } from 'src/gql/generated';
import { getScrollParent } from 'src/helpers/dom';
import { appColors } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { ExternalToolSwitch } from './external-tool-switch';
import { writeExternalToolkitCatalogue } from './external-toolkit-catalogue-cache';
import { getExternalToolkitMutationErrorMessage } from './external-toolkit-errors';
import { ExternalToolkitTools } from './external-toolkit-tools';
import {
  IntegrationActionButton,
  integrationHoverActionRevealStyles,
} from './IntegrationActionButton';
import { useExternalToolkitConnectLink } from './use-external-toolkit-connect';

interface ExternalToolkitRowProps {
  readonly toolkit: Apollo.ExternalToolkitSimpleFragment;
  readonly isOrgAdmin?: boolean;
  readonly onRedirect?: (redirectUrl: string) => void;
  /** Organization allow/block. Admin-only; members omit it. */
  readonly orgAction?: JSX.Element;
}

const useStyles = makeStyles()(theme => ({
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 2, 2),
    textAlign: 'center',
  },
  header: {
    alignItems: 'center',
    backgroundColor: '#FFF',
    borderTopLeftRadius: theme.spacing(1.5),
    borderTopRightRadius: theme.spacing(1.5),
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
  },
  headerActions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  headerCollapsed: {
    borderBottomLeftRadius: theme.spacing(1.5),
    borderBottomRightRadius: theme.spacing(1.5),
  },
  /* Shadow, not border: a border would change header height and jump the group bars. */
  headerExpanded: {
    boxShadow: `0 1px 0 ${theme.palette.divider}`,
  },
  headerIcon: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    transition: theme.transitions.create('transform', { duration: 150 }),
  },
  headerIconOpen: {
    transform: 'rotate(180deg)',
  },
  /**
   * Only while the bucket is open. A negative `top` on every card pulled the
   * white header over the paper's top stroke, which is why collapsed rows
   * looked clipped.
   */
  headerSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  /**
   * Admin content keeps 8px of padding on the scrollport. Once this header is
   * stuck, that padding plus the 1px card border would show tool text through.
   * Paint over them only while stuck so the resting border stays intact.
   */
  headerStuck: {
    '&::before': {
      backgroundColor: '#FFF',
      content: '""',
      height: `calc(${theme.spacing(1)} + 1px)`,
      left: -1,
      pointerEvents: 'none',
      position: 'absolute',
      right: -1,
      top: `calc(-${theme.spacing(1)} - 1px)`,
    },
  },
  headerTrigger: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    gap: theme.spacing(2),
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  name: {
    color: appColors.strongType,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 'normal',
  },
  paper: {
    ...integrationHoverActionRevealStyles,
    backgroundColor: '#FFF',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    boxShadow: 'none',
    overflow: 'hidden',
  },
  /**
   * Sticky Read / Write bars need a visible overflow ancestor. Later cards
   * also paint after this one, so the open card gets a stacking context or
   * Outlook slides over Gmail's stuck header.
   */
  paperExpanded: {
    overflow: 'visible',
    position: 'relative',
    zIndex: 2,
  },
  reconnectPaths: {
    background: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
  },
  status: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  statusDot: {
    borderRadius: '50%',
    flexShrink: 0,
    height: 8,
    width: 8,
  },
  statusDotConnected: {
    backgroundColor: theme.appColors.connectedCheck,
  },
  statusDotExpired: {
    backgroundColor: theme.palette.warning.main,
  },
  statusDotNotConnected: {
    backgroundColor: theme.palette.action.disabled,
  },
  statusLine: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  stickySentinel: {
    height: 1,
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  tools: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

function useConnectionActions(
  toolkit: Apollo.ExternalToolkitSimpleFragment,
  onRedirect: (redirectUrl: string) => void
) {
  const { enqueueSnackbar } = useSnackbar();
  const { loading: connecting, requestConnectLink } = useExternalToolkitConnectLink({
    refetchQueries: [Apollo.GetExternalToolkitsDocument],
  });
  const [disconnect, { loading: disconnecting }] = Apollo.useDisconnectExternalToolkitMutation({
    awaitRefetchQueries: true,
    refetchQueries: [Apollo.GetExternalToolkitsDocument],
  });
  const handleConnect = useCallback(async () => {
    try {
      onRedirect(await requestConnectLink(toolkit.slug));
    } catch (error) {
      enqueueSnackbar(
        getExternalToolkitMutationErrorMessage(
          error,
          `Could not connect ${toolkit.name}. Try again.`
        ),
        { variant: 'error' }
      );
    }
  }, [enqueueSnackbar, onRedirect, requestConnectLink, toolkit.name, toolkit.slug]);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const requestDisconnect = useCallback(() => setConfirmingDisconnect(true), []);
  const cancelDisconnect = useCallback(() => setConfirmingDisconnect(false), []);
  const confirmDisconnect = useCallback(async () => {
    try {
      await disconnect({ variables: { toolkit: toolkit.slug } });
    } catch (error) {
      enqueueSnackbar(
        getExternalToolkitMutationErrorMessage(
          error,
          `Could not disconnect ${toolkit.name}. Try again.`
        ),
        { variant: 'error' }
      );
    }
  }, [disconnect, enqueueSnackbar, toolkit.name, toolkit.slug]);
  return {
    busy: connecting || disconnecting,
    cancelDisconnect,
    confirmDisconnect,
    confirmingDisconnect,
    handleConnect,
    requestDisconnect,
  };
}

function useGrantAction(toolkit: Apollo.ExternalToolkitSimpleFragment) {
  const { enqueueSnackbar } = useSnackbar();
  const [setGrants, { loading }] = Apollo.useSetExternalToolGrantsMutation({
    refetchQueries: [Apollo.GetExternalToolkitsDocument],
  });
  const handleGrant = useCallback(
    async (toolSlugs: readonly string[], enabled: boolean) => {
      if (toolSlugs.length === 0) return;
      try {
        await setGrants({
          optimisticResponse: getGrantOptimisticResponse(toolkit, toolSlugs, enabled),
          update(cache, { data }) {
            const next = data?.setExternalToolGrants;
            if (!next) return;

            writeExternalToolkitCatalogue(cache, next);
          },
          variables: { enabled, toolSlugs: [...toolSlugs], toolkit: toolkit.slug },
        });
      } catch (error) {
        enqueueSnackbar(
          getExternalToolkitMutationErrorMessage(
            error,
            `Could not update ${toolkit.name} tool access. Try again.`
          ),
          { variant: 'error' }
        );
      }
    },
    [enqueueSnackbar, setGrants, toolkit]
  );
  return { busy: loading, handleGrant };
}

interface ConnectionActions {
  readonly busy: boolean;
  readonly cancelDisconnect: () => void;
  readonly confirmDisconnect: () => Promise<void>;
  readonly confirmingDisconnect: boolean;
  readonly handleConnect: () => void;
  readonly requestDisconnect: () => void;
}

const PolicyDeniedNotice = React.memo(function PolicyDeniedNotice({
  isOrgAdmin,
  toolkit,
}: {
  readonly isOrgAdmin: boolean;
  readonly toolkit: Apollo.ExternalToolkitSimpleFragment;
}): JSX.Element {
  const { classes } = useStyles();
  const message = isOrgAdmin
    ? `${toolkit.name} is disabled for your organization. Enable it in settings to use its tools.`
    : `${toolkit.name} is disabled by your organization. Contact an admin to enable it.`;
  return (
    <Typography className={classes.empty} variant="body2">
      {message}
    </Typography>
  );
});

interface GrantAction {
  readonly busy: boolean;
  readonly handleGrant: (toolSlugs: readonly string[], enabled: boolean) => void;
}

const ToolkitIdentity = React.memo(function ToolkitIdentity({
  toolkit,
}: {
  readonly toolkit: Apollo.ExternalToolkitSimpleFragment;
}): JSX.Element {
  const { classes, cx } = useStyles();
  return (
    <span className={classes.identity}>
      <Typography className={classes.name} component="span" variant="subtitle1">
        {toolkit.name}
      </Typography>
      <span className={classes.statusLine}>
        <span
          className={cx(
            classes.statusDot,
            classes[getStatusDotClass(toolkit.allowedByOrg, toolkit.status)]
          )}
        />
        <Typography className={classes.status} component="span" variant="body2">
          {getToolkitStatusText(toolkit)}
        </Typography>
      </span>
    </span>
  );
});

interface ToolkitActionProps {
  readonly allowed: boolean;
  readonly busy: boolean;
  readonly onConnect: () => void;
  readonly onDisconnect: () => void;
  readonly status: Apollo.ExternalToolkitConnectionStatus;
}

const ToolkitAction = React.memo(function ToolkitAction({
  allowed,
  busy,
  onConnect,
  onDisconnect,
  status,
}: ToolkitActionProps): JSX.Element | null {
  const hasAccount = status !== Apollo.ExternalToolkitConnectionStatus.NotConnected;
  if (!allowed && !hasAccount) return null;
  if (status === Apollo.ExternalToolkitConnectionStatus.Connected || !allowed) {
    return (
      <IntegrationActionButton disabled={busy} variant="outlined" onClick={onDisconnect}>
        Disconnect
      </IntegrationActionButton>
    );
  }
  return (
    <IntegrationActionButton disabled={busy} variant="outlined" onClick={onConnect}>
      {status === Apollo.ExternalToolkitConnectionStatus.Expired ? 'Reconnect' : 'Connect'}
    </IntegrationActionButton>
  );
});

const ToolkitHeader = React.memo(function ToolkitHeader({
  busy,
  connection,
  expanded,
  headerRef,
  onToggle,
  orgAction,
  stuck,
  toolkit,
  toolsPanelId,
}: {
  readonly busy: boolean;
  readonly connection: ConnectionActions;
  readonly expanded: boolean;
  readonly headerRef: React.Ref<HTMLDivElement>;
  readonly onToggle: () => void;
  readonly stuck: boolean;
  readonly toolkit: Apollo.ExternalToolkitSimpleFragment;
  readonly toolsPanelId: string;
  readonly orgAction?: JSX.Element;
}): JSX.Element {
  const { classes, cx } = useStyles();
  return (
    <div
      ref={headerRef}
      className={cx(
        classes.header,
        !expanded && classes.headerCollapsed,
        expanded && classes.headerSticky,
        expanded && classes.headerExpanded,
        stuck && classes.headerStuck
      )}
    >
      <ButtonBase
        aria-controls={toolsPanelId}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${toolkit.name} tools`}
        className={classes.headerTrigger}
        onClick={onToggle}
      >
        <ChevronDown
          className={cx(classes.headerIcon, expanded && classes.headerIconOpen)}
          size={20}
        />
        <ToolkitIdentity toolkit={toolkit} />
      </ButtonBase>
      <div className={classes.headerActions}>
        {orgAction}
        <ToolkitAction
          allowed={toolkit.allowedByOrg}
          busy={busy}
          status={toolkit.status}
          onConnect={connection.handleConnect}
          onDisconnect={connection.requestDisconnect}
        />
      </div>
    </div>
  );
});

const ToolkitTools = React.memo(function ToolkitTools({
  busy,
  grant,
  groupStickyTop,
  toolkit,
}: {
  readonly busy: boolean;
  readonly grant: GrantAction;
  readonly groupStickyTop: string;
  readonly toolkit: Apollo.ExternalToolkitSimpleFragment;
}): JSX.Element | null {
  const { classes } = useStyles();
  const renderTool = useCallback(
    (tool: Apollo.ExternalToolkitSimpleFragment['tools'][number]) => (
      <ExternalToolSwitch
        key={tool.slug}
        allowed={toolkit.allowedByOrg}
        busy={busy}
        tool={tool}
        onToggle={grant.handleGrant}
      />
    ),
    [busy, grant.handleGrant, toolkit.allowedByOrg]
  );

  if (!toolkit.catalogueAvailable) {
    return (
      <Typography className={classes.empty} variant="body2">
        Tool catalogue is unavailable. Refresh to try again.
      </Typography>
    );
  }

  const connectCopy = getConnectToolsCopy(toolkit.name, toolkit.status);
  if (connectCopy && toolkit.tools.length === 0) {
    if (!toolkit.allowedByOrg) return null;
    return (
      <Typography className={classes.empty} variant="body2">
        {connectCopy}
      </Typography>
    );
  }

  return (
    <div className={classes.tools}>
      <ExternalToolkitTools
        allowedByOrg={toolkit.allowedByOrg}
        busy={busy}
        groupStickyTop={groupStickyTop}
        renderTool={renderTool}
        toolkitName={toolkit.name}
        tools={toolkit.tools}
        onToggleTools={grant.handleGrant}
      />
    </div>
  );
});

const ToolkitContents = React.memo(function ToolkitContents({
  busy,
  grant,
  groupStickyTop,
  isOrgAdmin,
  toolkit,
}: {
  readonly busy: boolean;
  readonly grant: GrantAction;
  readonly groupStickyTop: string;
  readonly isOrgAdmin: boolean;
  readonly toolkit: Apollo.ExternalToolkitSimpleFragment;
}): JSX.Element | null {
  if (!toolkit.allowedByOrg)
    return <PolicyDeniedNotice isOrgAdmin={isOrgAdmin} toolkit={toolkit} />;

  return (
    <ToolkitTools busy={busy} grant={grant} groupStickyTop={groupStickyTop} toolkit={toolkit} />
  );
});

/** Explain the distinct chat and settings reconnection paths. */
const DisconnectReconnectPaths = React.memo(function DisconnectReconnectPaths({
  enabledCount,
}: {
  readonly enabledCount: number;
}): JSX.Element | null {
  const { classes } = useStyles();
  if (enabledCount === 0) return null;
  return (
    <div className={classes.reconnectPaths}>
      <Typography variant="body2">
        <strong>Getting them back</strong>
      </Typography>
      <Typography color="text.secondary" variant="body2">
        If a chat later asks you to reconnect, your selections come back with the account.
      </Typography>
      <Typography color="text.secondary" variant="body2">
        If you reconnect from this page, they stay off until you enable them again.
      </Typography>
    </div>
  );
});

const DisconnectConfirmation = React.memo(function DisconnectConfirmation({
  connection,
  toolkit,
}: {
  readonly connection: ConnectionActions;
  readonly toolkit: Apollo.ExternalToolkitSimpleFragment;
}): JSX.Element {
  return (
    <ConfirmationDialog
      message={getDisconnectMessage(toolkit.name, toolkit.enabledCount)}
      open={connection.confirmingDisconnect}
      primaryText="Disconnect"
      title={`Disconnect ${toolkit.name}?`}
      variant={ConfirmationDialogVariant.Warn}
      onClickPrimary={connection.confirmDisconnect}
      onClose={connection.cancelDisconnect}
    >
      <DisconnectReconnectPaths enabledCount={toolkit.enabledCount} />
    </ConfirmationDialog>
  );
});

function ExternalToolkitRowView({
  isOrgAdmin = false,
  onRedirect = assignRedirect,
  orgAction,
  toolkit,
}: ExternalToolkitRowProps): JSX.Element {
  const { classes, cx } = useStyles();
  const { headerRef, height: headerHeight } = useToolkitHeaderHeight();
  const connection = useConnectionActions(toolkit, onRedirect);
  const grant = useGrantAction(toolkit);
  const [expanded, setExpanded] = useState(false);
  const handleToggle = useCallback(() => setExpanded(current => !current), []);
  const { sentinelRef, stuck } = useToolkitHeaderStuck(expanded);
  const busy = connection.busy || grant.busy;
  const toolsPanelId = `external-toolkit-${toolkit.slug}-tools`;
  const groupStickyTop = `${headerHeight}px`;
  return (
    <Paper className={cx(classes.paper, expanded && classes.paperExpanded)} elevation={0}>
      {expanded && <div ref={sentinelRef} aria-hidden className={classes.stickySentinel} />}
      <ToolkitHeader
        busy={busy}
        connection={connection}
        expanded={expanded}
        headerRef={headerRef}
        orgAction={orgAction}
        stuck={stuck}
        toolkit={toolkit}
        toolsPanelId={toolsPanelId}
        onToggle={handleToggle}
      />
      {expanded && (
        <div aria-label={`${toolkit.name} tools`} id={toolsPanelId} role="region">
          <ToolkitContents
            busy={connection.busy}
            grant={grant}
            groupStickyTop={groupStickyTop}
            isOrgAdmin={isOrgAdmin}
            toolkit={toolkit}
          />
        </div>
      )}
      <DisconnectConfirmation connection={connection} toolkit={toolkit} />
    </Paper>
  );
}

export const ExternalToolkitRow = React.memo(ExternalToolkitRowView);

/**
 * Height of the toolkit header, so access-group bars can stick under it.
 *
 * Re-observing on a tab show is a no-op when the height has not changed.
 */
function useToolkitHeaderHeight() {
  const headerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const node = headerRef.current;
    if (!node) return undefined;
    const update = () => {
      const next = node.offsetHeight;
      setHeight(current => (current === next ? current : next));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { headerRef, height };
}

/**
 * Whether the toolkit header has engaged sticky.
 *
 * A 1px sentinel sits at the top of the open card. When it leaves the admin
 * scroller, the header is stuck. Re-observing on a tab show is a no-op when
 * the stuck flag has not changed.
 */
function useToolkitHeaderStuck(active: boolean) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  useLayoutEffect(() => {
    if (!active) {
      setStuck(current => (current ? false : current));
      return undefined;
    }
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const root = getScrollParent(sentinel);
    if (!root) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const next = !entry.isIntersecting;
        setStuck(current => (current === next ? current : next));
      },
      { root, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [active]);
  return { sentinelRef, stuck };
}

function getGrantOptimisticResponse(
  toolkit: Apollo.ExternalToolkitSimpleFragment,
  toolSlugs: readonly string[],
  enabled: boolean
): Apollo.SetExternalToolGrantsMutation {
  const changedSlugs = new Set(toolSlugs);
  const tools = toolkit.tools.map(tool =>
    changedSlugs.has(tool.slug) ? { ...tool, enabled } : tool
  );
  return {
    __typename: 'Mutation',
    setExternalToolGrants: {
      ...toolkit,
      enabledCount: tools.filter(tool => tool.enabled).length,
      tools,
    },
  };
}

/** What disconnecting does now, before the two reconnect paths are laid out. */
function getDisconnectMessage(name: string, enabledCount: number): string {
  if (enabledCount === 0) return `Aline stops using ${name} right away.`;
  const tools =
    enabledCount === 1 ? 'the 1 tool you enabled is' : `the ${enabledCount} tools you enabled are`;
  return `Aline stops using ${name} right away, and ${tools} switched off.`;
}

function getStatusLabel(status: Apollo.ExternalToolkitConnectionStatus): string {
  if (status === Apollo.ExternalToolkitConnectionStatus.Connected) return 'Connected';
  if (status === Apollo.ExternalToolkitConnectionStatus.Expired) return 'Connection expired';
  return 'Not connected';
}

function getToolkitStatusText(toolkit: Apollo.ExternalToolkitSimpleFragment): string {
  if (!toolkit.allowedByOrg) return 'Disabled by organization';
  return `${getStatusLabel(toolkit.status)} · ${getToolCountLabel(toolkit.enabledCount)}`;
}

function getConnectToolsCopy(
  name: string,
  status: Apollo.ExternalToolkitConnectionStatus
): string | null {
  if (status === Apollo.ExternalToolkitConnectionStatus.NotConnected)
    return `Click Connect to authorize ${name}.`;
  if (status === Apollo.ExternalToolkitConnectionStatus.Expired)
    return `Click Reconnect to authorize ${name}.`;
  return null;
}

/**
 * Read at call time rather than from a module-level lookup.
 *
 * Tests mock the generated module, so the status values do not exist while
 * this file is being imported.
 */
function getStatusDotClass(
  allowedByOrg: boolean,
  status: Apollo.ExternalToolkitConnectionStatus
): 'statusDotConnected' | 'statusDotExpired' | 'statusDotNotConnected' {
  if (!allowedByOrg) return 'statusDotNotConnected';
  if (status === Apollo.ExternalToolkitConnectionStatus.Connected) return 'statusDotConnected';
  if (status === Apollo.ExternalToolkitConnectionStatus.Expired) return 'statusDotExpired';
  return 'statusDotNotConnected';
}

function getToolCountLabel(enabledCount: number): string {
  return enabledCount === 1 ? '1 tool enabled' : `${enabledCount} tools enabled`;
}

function assignRedirect(redirectUrl: string): void {
  window.location.assign(redirectUrl);
}
